/**
 * One-shot handoff between Moo Studio (`/moo/studio/*`) and the Ask Moo panel.
 *
 * The panel (`AskMooIntegrationV2`) is mounted by `ConsoleLayout`, while the
 * `/moo/*` routes are a separate top-level branch in `AuthenticatedRouter`.
 * Studio therefore has no panel to reach: it can't read the launcher state and
 * a window event would have no listener mounted. So the two sides hand off
 * through session storage instead — opening Studio records the route the user
 * came from, and Studio's "Back to Ask Moo" navigates back there and leaves a
 * flag that the panel consumes on mount to open itself.
 *
 * Both values are read-once and per-tab. `sessionStorage` can throw (Safari
 * private mode, quota, group policy), so every access degrades silently: the
 * worst case is landing on the persona home with the panel closed.
 */

const RETURN_PATH_KEY = 'moo.v2.studioReturnPath';
const OPEN_ON_MOUNT_KEY = 'moo.v2.openOnMount';

/** Current in-app path, query included. Guarded for environments with no
 *  `location`, mirroring the navigation adapter's `getCurrentPath`. Lives here
 *  because its only purpose is feeding {@link rememberStudioReturnPath}, and
 *  every caller that opens Studio needs the pair together. */
export function currentLocationPath(): string {
  if (globalThis.location === undefined) {
    return '';
  }
  return `${globalThis.location.pathname}${globalThis.location.search}`;
}

/** Records where the user was when they opened Moo Studio. */
export function rememberStudioReturnPath(path: string): void {
  if (!path) {
    return;
  }
  try {
    sessionStorage.setItem(RETURN_PATH_KEY, path);
  } catch {
    // disabled / quota — degrade silently
  }
}

/**
 * Reads and clears the route recorded by {@link rememberStudioReturnPath}.
 * Returns `null` when Studio was reached some other way (bookmark, direct URL,
 * a redirect), leaving the caller to pick its own fallback.
 */
export function takeStudioReturnPath(): string | null {
  try {
    const path = sessionStorage.getItem(RETURN_PATH_KEY);
    sessionStorage.removeItem(RETURN_PATH_KEY);
    return path;
  } catch {
    return null;
  }
}

/** Asks the Ask Moo panel to open as soon as it next mounts. */
export function requestAskMooOpenOnMount(): void {
  try {
    sessionStorage.setItem(OPEN_ON_MOUNT_KEY, '1');
  } catch {
    // disabled / quota — degrade silently
  }
}

/**
 * Reads and clears the flag set by {@link requestAskMooOpenOnMount}. Consuming
 * it on read keeps a later remount of the panel from reopening it after the
 * user has closed it.
 */
export function consumeAskMooOpenOnMount(): boolean {
  try {
    const requested = sessionStorage.getItem(OPEN_ON_MOUNT_KEY) !== null;
    sessionStorage.removeItem(OPEN_ON_MOUNT_KEY);
    return requested;
  } catch {
    return false;
  }
}
